import React, { useState, useRef, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

interface CustomOTPInputProps {
  value: string;
  onChange: (value: string) => void;
  numInputs?: number;
  shouldAutoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  error?: boolean;
  disabled?: boolean;
}

const CustomOTPInput: React.FC<CustomOTPInputProps> = ({
  value,
  onChange,
  numInputs = 6,
  shouldAutoFocus = true,
  className = '',
  inputClassName = '',
  error = false,
  disabled = false,
}) => {
  const [activeInput, setActiveInput] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, numInputs);
  }, [numInputs]);

  // Auto focus first input
  useEffect(() => {
    if (shouldAutoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [shouldAutoFocus]);

  // Handle input change
  const handleChange = useCallback((index: number, inputValue: string) => {
    // Only allow single digit
    const sanitizedValue = inputValue.replace(/[^0-9]/g, '');
    
    if (sanitizedValue.length > 1) {
      return; // Prevent multiple characters
    }

    const newValue = value.split('');
    newValue[index] = sanitizedValue;
    const updatedValue = newValue.join('');
    
    onChange(updatedValue);

    // Move to next input if value entered
    if (sanitizedValue && index < numInputs - 1) {
      setActiveInput(index + 1);
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 0);
    }
  }, [value, onChange, numInputs]);

  // Handle key down
  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    
    // Allow paste (Ctrl+V, Cmd+V)
    if (e.ctrlKey || e.metaKey) {
      return; // Don't prevent default for Ctrl/Cmd combinations
    }
    
    // Allow numbers, backspace, and navigation keys
    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
      return;
    }

    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      
      const newValue = value.split('');
      
      if (newValue[index]) {
        // If current input has value, clear it
        newValue[index] = '';
        onChange(newValue.join(''));
      } else if (index > 0) {
        // If current input is empty, move to previous and clear it
        newValue[index - 1] = '';
        onChange(newValue.join(''));
        setActiveInput(index - 1);
        setTimeout(() => {
          inputRefs.current[index - 1]?.focus();
        }, 0);
      }
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      setActiveInput(index - 1);
      setTimeout(() => {
        inputRefs.current[index - 1]?.focus();
      }, 0);
    }
    
    if (e.key === 'ArrowRight' && index < numInputs - 1) {
      setActiveInput(index + 1);
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 0);
    }
  }, [value, onChange, numInputs]);

  // Handle paste
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numbersOnly = pastedData.replace(/\D/g, '');
    
    if (numbersOnly.length <= numInputs) {
      // Fill inputs with pasted data
      const newValue = numbersOnly.padEnd(numInputs, '');
      onChange(newValue);
      
      // Focus the next empty input or last input
      const nextEmptyIndex = Math.min(numbersOnly.length, numInputs - 1);
      setActiveInput(nextEmptyIndex);
      setTimeout(() => {
        inputRefs.current[nextEmptyIndex]?.focus();
      }, 0);
    }
  }, [onChange, numInputs]);

  // Handle focus
  const handleFocus = useCallback((index: number) => {
    setActiveInput(index);
  }, []);

  // Handle click
  const handleClick = useCallback((index: number) => {
    setActiveInput(index);
    inputRefs.current[index]?.focus();
  }, []);

  return (
    <div className={twMerge('flex gap-[10px]', className)}>
      {Array.from({ length: numInputs }, (_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          onClick={() => handleClick(index)}
          disabled={disabled}
          className={twMerge(
            'border border-k-grey-light rounded-[12px] w-full flex-grow h-20 text-[44px] font-[264] focus:border-k-blue focus:border-[1.5px] outline-none text-center',
            value.length === numInputs && 'border-k-light-blue border-[1.5px]',
            error && '!border-k-error border-[1.5px]',
            disabled && 'opacity-50 cursor-not-allowed',
            inputClassName
          )}
        />
      ))}
    </div>
  );
};

export default CustomOTPInput;
